import { Field as ChakraField, Span, Flex, Box, RadioGroup, Checkbox } from '@chakra-ui/react';
import { convertUTCToTimezoneDate, DatePicker, formatDateUTC, FormInput, FormTextarea, i18n } from '@shared';
import { IoArrowDownOutline, IoArrowUpOutline, IoCheckmarkCircle, IoStar, IoStarOutline } from 'react-icons/io5';
import { getTranslation } from '../utils/translations';
import { Controller, useFormContext } from 'react-hook-form';
import { useSurveyContext } from '../context/SurveyContext';
import { darkenHex, lightenHex, applyOpacity, getReadableTextColor } from '../utils/colors';
import { useTranslation } from 'react-i18next';
import {
	BsEmojiAngry,
	BsEmojiFrown,
	BsEmojiNeutral,
	BsEmojiSmile,
	BsEmojiLaughing,
	BsEmojiAngryFill,
	BsEmojiFrownFill,
	BsEmojiLaughingFill,
	BsEmojiNeutralFill,
	BsEmojiSmileFill,
} from 'react-icons/bs';
import { DateTime } from 'luxon';

export type FieldType = {
	elementType: ElementType;
	orderNumber: number | null;
	isRequired: boolean;
	min: number | null;
	max: number | null;

	surveyElementID?: number;
	translations?: Translation[];
	surveyElementOptions?: SurveyElementOption[];
};

const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;
const FORM_INPUT_FIELDS = ['email', 'phone', 'text', 'number'];

export default function Field({ field }: { field: SurveyElement }) {
	const { setValue, watch } = useFormContext();
	const { survey } = useSurveyContext();
	const { t } = useTranslation();

	function getInputType(elementType: ElementType): string {
		if (elementType === 'email') {
			return 'email';
		}

		if (elementType === 'phone') {
			return 'tel';
		}

		if (elementType === 'number') {
			return 'number';
		}
		return 'text';
	}

	return (
		<Flex gap={4} width="100%" justifyContent={'space-between'} alignItems={'center'} className="tav-survey-field-container">
			<ChakraField.Root gap={0} required={field.isRequired}>
				{field.elementType !== 'pageBreak' && field.elementType !== 'divider' && field.elementType !== 'paragraph' ? (
					<>
						<ChakraField.Label marginBottom={1} wordBreak={'break-word'}>
							{getTranslation(field.translations, 'surveyElements.label', i18n.resolvedLanguage || 'fi')}
							<ChakraField.RequiredIndicator />
						</ChakraField.Label>
						{field.elementType === 'checkbox' && (field.min || field.max)
							? (() => {
									let message = '';

									if (field.min && field.max) {
										if (field.max === 1) {
											message = t('general.select_one');
										} else if (field.min === field.max) {
											message = `${t('general.select')} ${field.min}`;
										} else {
											message = `${t('general.select')} ${field.min} – ${field.max}`;
										}
									} else if (field.min) {
										message = `${t('general.select_at_least')} ${field.min}`;
									} else if (field.max) {
										message = `${t('general.select_maximum')} ${field.max}`;
									}

									return (
										<Span marginBottom={2} fontWeight={400} fontSize="12px">
											{message}
										</Span>
									);
								})()
							: null}
					</>
				) : null}
				{FORM_INPUT_FIELDS.includes(field.elementType) ? (
					<>
						<FormInput
							isRequired={field.isRequired}
							fieldRootProps={{ width: '100%' }}
							name={field.surveyElementID.toString()}
							inputProps={{
								placeholder: getTranslation(field.translations, 'surveyElements.placeholder', i18n.resolvedLanguage || 'fi', false),
							}}
							type={getInputType(field.elementType)}
							max={field.max}
							min={field.min}
							helperText={(field.elementType === 'number' && field.max) || field.min ? `(${field.min ? field.min : ''} - ${field.max ? field.max : ''})` : null}
						/>
					</>
				) : null}
				{field.elementType === 'textarea' ? (
					<FormTextarea
						textareaProps={{ rows: 3, placeholder: getTranslation(field.translations, 'surveyElements.placeholder', i18n.resolvedLanguage || 'fi', false) }}
						isRequired={field.isRequired}
						name={field.surveyElementID.toString()}
					/>
				) : null}
				{field.elementType === 'date' ? (
					<>
						<DatePicker
							colorScheme={survey.colorScheme}
							timeFormat="HH:mm"
							showTimeSelect
							dateFormat="dd.MM.yyyy - HH:mm"
							selected={watch(field.surveyElementID.toString()) ? convertUTCToTimezoneDate(watch(field.surveyElementID.toString()) as string) : null}
							onChange={(date: Date | null) => {
								setValue(field.surveyElementID.toString(), date instanceof Date ? formatDateUTC(date) : null, {
									shouldDirty: true,
								});
							}}
							placeholderText={getTranslation(field.translations, 'surveyElements.placeholder', i18n.resolvedLanguage || 'fi', false)}
							portalId="root-portal"
							popperClassName="date-picker-survey-field"
						/>
						<Span marginTop={1} fontSize={'12px'} color="rgb(82, 82, 91)">
							(UTC{DateTime.now().setZone(TIMEZONE).toFormat('ZZ')}) {TIMEZONE}
						</Span>
					</>
				) : null}
				{field.elementType === 'pageBreak' ? <PageBreak /> : null}
				{field.elementType === 'divider' ? <Divider /> : null}
				{field.elementType === 'smiley' ? <Smiley element={field} /> : null}
				{field.elementType === 'star' ? <Star element={field} /> : null}
				{field.elementType === 'nps' ? <Scale element={field} survey={survey} /> : null}
				{field.elementType === 'scale' ? <Scale element={field} survey={survey} /> : null}
				{field.elementType === 'paragraph' ? (
					<Box dangerouslySetInnerHTML={{ __html: getTranslation(field.translations, 'surveyElements.label', i18n.resolvedLanguage || 'fi') }} />
				) : null}
				{field.elementType === 'checkbox' && <CheckboxField field={field} />}
			</ChakraField.Root>
		</Flex>
	);
}

function CheckboxField({ field }: { field: SurveyElement }) {
	const { t } = useTranslation();
	const { survey } = useSurveyContext();
	const { control } = useFormContext();

	return (
		<Flex marginBottom={2} marginTop={2} flexDirection="column" gap={2} width="100%">
			<Controller
				rules={{
					...(field.isRequired && {
						required: t('general.required'),
					}),
					validate: {
						...(field.min && {
							minOptions: (value: number[]) => value?.length >= (field.min || 0) || t('general.min_options', { count: field.min || 0 }),
						}),
						...(field.max && {
							maxOptions: (value: number[]) => value?.length <= (field.max || 999) || t('general.max_options', { count: field.max || 0 }),
						}),
					},
				}}
				name={field.surveyElementID.toString()}
				control={control}
				render={({ field: controlField, fieldState }) => {
					const selectedValues = Array.isArray(controlField.value) ? controlField.value : [];

					const handleValueChange = (optionID: number) => {
						const isSelected = selectedValues.includes(optionID);

						if (isSelected) {
							controlField.onChange(selectedValues.filter(id => id !== optionID));
							return;
						}

						if (field.max === 1) {
							controlField.onChange([optionID]);
							return;
						}

						if (field.max && selectedValues.length >= field.max) {
							return; // block selecting beyond max
						}

						controlField.onChange([...selectedValues, optionID]);
					};

					return (
						<ChakraField.Root as="fieldset" invalid={fieldState.invalid} required={field.isRequired} colorPalette={'brand.primary'}>
							{field.surveyElementOptions
								?.sort((a, b) => {
									const aOrder = a.orderNumber ?? Infinity;
									const bOrder = b.orderNumber ?? Infinity;
									return aOrder - bOrder;
								})
								.map(option => {
									const isChecked = selectedValues.includes(option.surveyElementOptionID);

									function getBackgroundColorForOption(): string {
										if (isChecked) {
											return applyOpacity(lightenHex(survey.colorScheme, 20), 0.25);
										}

										if (controlField.value === undefined) {
											return 'white';
										}

										if (controlField.value.length === field.max) {
											return '#eeeeee';
										}
										return 'white';
									}

									return (
										<Checkbox.Root
											key={option.surveyElementOptionID}
											colorPalette="brand-primary"
											onClick={() => handleValueChange(option.surveyElementOptionID)}
											width="100%"
											borderRadius={'8px'}
											padding={2}
											border={isChecked ? `1px ${darkenHex(survey.colorScheme, 20)} solid` : '1px solid #EEEEEE'}
											backgroundColor={getBackgroundColorForOption()}
											display={'flex'}
											justifyContent={'space-between'}
											boxSizing={'border-box'}
										>
											<Checkbox.Label wordBreak={'break-word'}>
												{getTranslation(option.translations, 'surveyElementOptions.label', i18n.resolvedLanguage || 'fi', true)}
											</Checkbox.Label>
											<Span marginLeft="auto">
												<IoCheckmarkCircle size="25" color={darkenHex(survey.colorScheme, 20)} style={{ opacity: isChecked ? 1 : 0 }} />
											</Span>
										</Checkbox.Root>
									);
								})}

							<ChakraField.ErrorText>{fieldState.error?.message}</ChakraField.ErrorText>
						</ChakraField.Root>
					);
				}}
			/>
		</Flex>
	);
}

function Divider() {
	return <hr style={{ width: '100%', marginBottom: '5px' }} />;
}

function Star({ element }: { element: SurveyElement }) {
	const { survey } = useSurveyContext();
	const { control } = useFormContext();
	const { t } = useTranslation();
	const min = 1; // default to 0 if null/undefined
	const max = 5; // default to 0 if null/undefined

	const options = Array.from({ length: max - min + 1 }, (_, i) => (min + i).toString());

	function getStar(isSelected: boolean) {
		return isSelected ? <IoStar fill={survey.colorScheme} /> : <IoStarOutline color={survey.colorScheme} />;
	}

	return (
		<Controller
			name={element.surveyElementID.toString()}
			control={control}
			rules={{
				...(element.isRequired && {
					required: t('general.required'),
				}),
			}}
			render={({ field: controlField, fieldState }) => {
				return (
					<ChakraField.Root as="fieldset" invalid={fieldState.invalid} required={element.isRequired} colorPalette={'brand.primary'}>
						<RadioGroup.Root
							onValueChange={({ value }) => {
								controlField.onChange(value);
							}}
							display="flex"
							flexDirection="row"
							rowGap={1}
							columnGap={2}
							flexWrap="wrap"
							width="100%"
						>
							{options.map(option => {
								return (
									<RadioGroup.Item
										width="40px"
										alignItems="center"
										justifyContent="center"
										height="40px"
										key={option}
										value={option}
										boxSizing="border-box"
										asChild={true}
										onClick={() => controlField.onChange(option)}
									>
										{getStar(controlField.value === option || controlField.value > option)}
									</RadioGroup.Item>
								);
							})}
						</RadioGroup.Root>
						<ChakraField.ErrorText>{fieldState.error?.message}</ChakraField.ErrorText>
					</ChakraField.Root>
				);
			}}
		/>
	);
}

function Smiley({ element }: { element: SurveyElement }) {
	const { survey } = useSurveyContext();
	const { control } = useFormContext();
	const { t } = useTranslation();
	const min = 1; // default to 0 if null/undefined
	const max = 5; // default to 0 if null/undefined

	const options = Array.from({ length: max - min + 1 }, (_, i) => (min + i).toString());

	function getSmileyByIndex(index: number, isSelected: boolean) {
		const smileys = [
			isSelected ? <BsEmojiAngryFill fill={survey.colorScheme} /> : <BsEmojiAngry fill={survey.colorScheme} />,
			isSelected ? <BsEmojiFrownFill fill={survey.colorScheme} /> : <BsEmojiFrown fill={survey.colorScheme} />,
			isSelected ? <BsEmojiNeutralFill fill={survey.colorScheme} /> : <BsEmojiNeutral fill={survey.colorScheme} />,
			isSelected ? <BsEmojiSmileFill fill={survey.colorScheme} /> : <BsEmojiSmile fill={survey.colorScheme} />,
			isSelected ? <BsEmojiLaughingFill fill={survey.colorScheme} /> : <BsEmojiLaughing fill={survey.colorScheme} />,
		];

		return smileys[index];
	}

	return (
		<Controller
			name={element.surveyElementID.toString()}
			control={control}
			rules={{
				...(element.isRequired && {
					required: t('general.required'),
				}),
			}}
			render={({ field: controlField, fieldState }) => {
				return (
					<ChakraField.Root as="fieldset" invalid={fieldState.invalid} required={element.isRequired} colorPalette={'brand.primary'}>
						<RadioGroup.Root
							onValueChange={({ value }) => {
								controlField.onChange(value);
							}}
							display="flex"
							flexDirection="row"
							rowGap={1}
							columnGap={2}
							flexWrap="wrap"
							width="100%"
						>
							{options.map((option, index) => {
								return (
									<RadioGroup.Item
										width="40px"
										alignItems="center"
										justifyContent="center"
										height="40px"
										key={option}
										value={option}
										boxSizing="border-box"
										asChild={true}
										onClick={() => controlField.onChange(option)}
									>
										{getSmileyByIndex(index, controlField.value === option)}
									</RadioGroup.Item>
								);
							})}
						</RadioGroup.Root>
						<ChakraField.ErrorText>{fieldState.error?.message}</ChakraField.ErrorText>
					</ChakraField.Root>
				);
			}}
		/>
	);
}

function Scale({ element, survey }: { element: SurveyElement; survey: Survey }) {
	const { control } = useFormContext();
	const { t } = useTranslation();

	if (element.min === null || element.max === null) return null;

	const min = element.min ?? 0; // default to 0 if null/undefined
	const max = element.max ?? 0; // default to 0 if null/undefined

	const options = Array.from({ length: max - min + 1 }, (_, i) => (min + i).toString());

	return (
		<Controller
			name={element.surveyElementID.toString()}
			control={control}
			rules={{
				...(element.isRequired && {
					required: t('general.required'),
				}),
			}}
			render={({ field: controlField, fieldState }) => {
				const selectedValue = parseInt(controlField.value, 10);

				return (
					<ChakraField.Root as="fieldset" invalid={fieldState.invalid} required={element.isRequired} colorPalette={'brand.primary'}>
						<RadioGroup.Root
							name={controlField.name}
							value={controlField.value}
							display="flex"
							flexDirection="row"
							rowGap={1}
							columnGap={2}
							flexWrap="wrap"
							onValueChange={({ value }) => controlField.onChange(value)}
							width="100%"
						>
							{options.map(option => {
								const optionValue = parseInt(option, 10);
								let backgroundColor = '#ffffff'; // default

								if (optionValue < selectedValue) backgroundColor = '#E2E2EA'; // grey before selected
								if (optionValue === selectedValue) backgroundColor = survey.colorScheme; // selected

								return (
									<RadioGroup.Item
										borderRadius="50%"
										width="40px"
										alignItems="center"
										justifyContent="center"
										height="40px"
										key={option}
										value={option}
										padding={2}
										backgroundColor={backgroundColor}
										border={controlField.value === option ? `1px solid ${survey.colorScheme}` : '1px solid #E2E2EA'}
										boxSizing="border-box"
									>
										<RadioGroup.ItemHiddenInput />
										<RadioGroup.ItemText color={getReadableTextColor(backgroundColor)} fontWeight={700}>
											{option}
										</RadioGroup.ItemText>
									</RadioGroup.Item>
								);
							})}
						</RadioGroup.Root>
						<ChakraField.ErrorText>{fieldState.error?.message}</ChakraField.ErrorText>
					</ChakraField.Root>
				);
			}}
		/>
	);
}

function PageBreak() {
	const { survey } = useSurveyContext();
	return (
		<Flex flexDirection={'column'} width="100%" alignItems={'center'}>
			<IoArrowUpOutline style={{ marginBottom: '-5px' }} size="25" color="rgb(61, 213, 152)" />
			<Box borderBottom={`3px ${survey.colorScheme}`} height="15px" borderTop={`3px ${survey.colorScheme} solid`} width="100%"></Box>
			<IoArrowDownOutline style={{ marginTop: '-5px' }} size="25" color="rgb(61, 213, 152)" />
		</Flex>
	);
}
